import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditExpenseModal = ({ isOpen, onClose, onSuccess, expense, token }) => {
  const [formData, setFormData] = useState({
    property_id: '',
    category: 'maintenance',
    amount: '',
    description: '',
    expense_date: '',
    payment_method: 'cash',
    receipt_url: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && expense) {
      setFormData({
        property_id: expense.property_id || '',
        category: expense.category || 'maintenance',
        amount: expense.amount?.toString() || '',
        description: expense.description || '',
        expense_date: expense.expense_date || '',
        payment_method: expense.payment_method || 'cash',
        receipt_url: expense.receipt_url || ''
      });
    }
  }, [isOpen, expense]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      await axios.put(`${API}/money/expenses/${expense.id}`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      emit(EVT.EXPENSE, { action: 'updated', id: expense.id });

      toast({
        title: "Expense Updated!",
        description: "Expense record has been updated successfully"
      });

      onSuccess();
      onClose();
    } catch (error) {
      let errorMessage = "Failed to update expense";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || err.message).join(', ');
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!expense) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Expense</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category *</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="maintenance">Maintenance</option>
                <option value="utilities">Utilities</option>
                <option value="repairs">Repairs</option>
                <option value="cleaning">Cleaning</option>
                <option value="supplies">Supplies</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="5000"
                required
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <Label>Expense Date *</Label>
              <Input
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData({...formData, expense_date: e.target.value})}
                required
              />
            </div>

            <div>
              <Label>Payment Method *</Label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
              </select>
            </div>

            <div className="col-span-2">
              <Label>Description *</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Describe the expense..."
                required
              />
            </div>

            <div className="col-span-2">
              <Label>Receipt URL</Label>
              <Input
                type="url"
                value={formData.receipt_url}
                onChange={(e) => setFormData({...formData, receipt_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Updating...' : 'Update Expense'}
            </Button>
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditExpenseModal;
